/**
 * Passcode Unlock Page
 * Allows user to unlock the app with their PIN or password
 * Includes brute-force protection with cooldowns
 */
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePin } from '../context/PinContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { Shield, Lock, Eye, EyeOff, AlertTriangle, Trash2, Clock } from 'lucide-react';
import { toast } from 'sonner';

export default function PinUnlockPage() {
  const navigate = useNavigate();
  const { unlock, wipeAllAppData, isPinSet, isUnlocked, failedAttempts, cooldownUntil, clearCooldown, passcodeType } = usePin();
  const [passcode, setPasscode] = useState('');
  const [showPasscode, setShowPasscode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [remainingTime, setRemainingTime] = useState(0);
  // Track if unlock was successful to prevent double-submit issues
  const [unlockSuccess, setUnlockSuccess] = useState(false);
  // Track if wipe is in progress
  const [wiping, setWiping] = useState(false);
  
  // Use ref to track current passcode value to avoid stale closure issues
  const passcodeRef = useRef('');

  // Handle redirects in useEffect to avoid React navigation errors
  useEffect(() => {
    if (!isPinSet) {
      navigate('/setup-pin', { replace: true });
    } else if (isUnlocked || unlockSuccess) {
      navigate('/dashboard', { replace: true });
    }
  }, [isPinSet, isUnlocked, unlockSuccess, navigate]);

  // Cooldown timer
  useEffect(() => {
    if (!cooldownUntil) {
      setRemainingTime(0);
      return;
    }

    const updateTimer = () => {
      const remaining = Math.max(0, cooldownUntil - Date.now());
      setRemainingTime(remaining);
      
      if (remaining <= 0) {
        clearCooldown();
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [cooldownUntil, clearCooldown]);

  // If redirecting, show nothing
  if (!isPinSet || isUnlocked || unlockSuccess) {
    return null;
  }

  const isInCooldown = remainingTime > 0;

  const formatTime = (ms) => {
    const seconds = Math.ceil(ms / 1000);
    if (seconds >= 60) {
      const minutes = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `${minutes}:${secs.toString().padStart(2, '0')}`;
    }
    return `${seconds} second${seconds !== 1 ? 's' : ''}`;
  };

  // Minimum length based on passcode type
  const minLength = passcodeType === 'password' ? 6 : 4;

  const handlePasscodeChange = (e) => {
    // Allow all characters for passwords, only digits for PINs
    const value = passcodeType === 'password' 
      ? e.target.value 
      : e.target.value.replace(/\D/g, '').slice(0, 8);
    passcodeRef.current = value;
    setPasscode(value);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent multiple submissions
    if (loading || unlockSuccess) {
      return;
    }
    
    // Read current passcode value from ref to avoid stale closure issues
    const currentPasscode = passcodeRef.current;
    
    setError('');

    if (isInCooldown) {
      return;
    }

    if (currentPasscode.length < minLength) {
      setError(`Passcode must be at least ${minLength} characters`);
      return;
    }

    setLoading(true);
    try {
      // Use currentPasscode (from ref) instead of passcode (state) to ensure we verify the actual input
      const result = await unlock(currentPasscode);
      
      if (result.success) {
        // Set local success state immediately to trigger navigation
        // This ensures we don't wait for context state to propagate
        setUnlockSuccess(true);
        // Clear passcode
        passcodeRef.current = '';
        setPasscode('');
        toast.success('Unlocked!');
        // Navigation will happen via useEffect watching unlockSuccess
      } else if (result.cooldown) {
        passcodeRef.current = '';
        setPasscode('');
        if (result.attempts >= 15) {
          setError('Too many attempts. Locked for 5 minutes.');
        } else if (result.attempts >= 10) {
          setError('Too many attempts. Locked for 60 seconds.');
        } else if (result.attempts >= 5) {
          setError('Too many attempts. Locked for 30 seconds.');
        }
        setLoading(false);
      } else {
        passcodeRef.current = '';
        setPasscode('');
        const attempts = result.attempts || failedAttempts + 1;
        if (attempts >= 4) {
          setError(`Incorrect passcode. ${5 - (attempts % 5) || 5} more attempts until lockout.`);
        } else {
          setError('Incorrect passcode. Please try again.');
        }
        setLoading(false);
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
      setLoading(false);
    }
  };

  const handleReset = async () => {
    // Prevent double-click
    if (wiping) {
      return;
    }
    
    setWiping(true);
    try {
      // Perform full data wipe (async)
      await wipeAllAppData();
      
      // Show success message
      toast.success('All data has been erased. Please set up a new passcode.');
      
      // Navigate to passcode setup
      navigate('/setup-pin', { replace: true });
    } catch (error) {
      console.error('[PinUnlock] Wipe error:', error);
      toast.error('Failed to reset app. Please try again.');
      setWiping(false);
    }
  };

  // Handle Enter key on input - use form submission for consistency
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && passcodeRef.current.length >= minLength && !isInCooldown && !loading && !unlockSuccess) {
      // Let the form's onSubmit handle it
      return;
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md flex-1 flex flex-col justify-center">
        {/* Logo/Brand */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-secondary/20 rounded-sm mb-4">
            <Shield className="w-10 h-10 text-secondary" />
          </div>
          <h1 className="text-3xl font-bold text-foreground font-['Chivo'] tracking-tight">
            ARMS ROOM
          </h1>
          <p className="text-lg font-semibold text-muted-foreground mt-2">
            Secure Firearm Inventory Management
          </p>
          {/* App Description */}
          <p className="mt-4 text-sm text-muted-foreground/80 leading-relaxed max-w-sm mx-auto">
            A private firearm inventory that stays on your device. Log firearms, ammo counts, accessories, and notes. Attach photos and documents for easy reference.
          </p>
          <p className="mt-2 text-sm font-medium text-muted-foreground/90">
            No accounts. No cloud. Just your inventory.
          </p>
        </div>

        {/* Passcode Unlock Card */}
        <Card className="bg-card border-border rounded-sm">
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-lg sm:text-xl flex items-center justify-center gap-2 leading-normal min-h-[1.75rem]">
              <Lock className="w-5 h-5 text-secondary flex-shrink-0" />
              <span className="whitespace-nowrap">Enter Passcode to Unlock</span>
            </CardTitle>
            <CardDescription>
              Your inventory is protected
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-4">
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Cooldown Warning */}
              {isInCooldown && (
                <div className="flex items-center justify-center gap-2 p-3 bg-destructive/10 border border-destructive/30 rounded-sm">
                  <Clock className="w-5 h-5 text-destructive" />
                  <span className="text-sm font-medium text-destructive">
                    Try again in {formatTime(remainingTime)}
                  </span>
                </div>
              )}

              {/* Passcode Input */}
              <div className="space-y-2">
                <Label htmlFor="passcode" className="sr-only">Passcode</Label>
                <div className="relative">
                  <Input
                    id="passcode"
                    type={showPasscode ? 'text' : 'password'}
                    value={passcode}
                    onChange={handlePasscodeChange}
                    onKeyDown={handleKeyDown}
                    placeholder={isInCooldown ? 'Locked' : 'ENTER'}
                    className={`pr-10 text-center text-2xl sm:text-3xl tracking-[0.3em] sm:tracking-[0.5em] font-mono h-14 ${
                      isInCooldown ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                    inputMode={passcodeType === 'password' ? 'text' : 'numeric'}
                    autoComplete="off"
                    autoFocus={!isInCooldown}
                    disabled={isInCooldown}
                    data-testid="unlock-pin-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasscode(!showPasscode)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    disabled={isInCooldown}
                  >
                    {showPasscode ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Passcode dots indicator */}
              <div className="flex justify-center gap-2">
                {[...Array(8)].map((_, i) => (
                  <div
                    key={i}
                    className={`w-3 h-3 rounded-full transition-all ${
                      i < passcode.length
                        ? 'bg-secondary scale-100'
                        : 'bg-muted scale-75'
                    }`}
                  />
                ))}
              </div>

              {/* Error Message */}
              {error && !isInCooldown && (
                <div className="flex items-center justify-center gap-2 text-destructive text-sm">
                  <AlertTriangle className="w-4 h-4" />
                  {error}
                </div>
              )}

              {/* Failed attempts indicator */}
              {failedAttempts > 0 && !isInCooldown && (
                <p className="text-center text-xs text-muted-foreground">
                  Failed attempts: {failedAttempts}
                </p>
              )}

              <Button
                type="submit"
                disabled={loading || passcode.length < minLength || isInCooldown}
                className="w-full h-12 rounded-sm"
                data-testid="unlock-btn"
              >
                {loading ? 'Unlocking...' : isInCooldown ? 'Locked' : 'Unlock'}
              </Button>
            </form>

            {/* Forgot Passcode */}
            <div className="mt-6 pt-4 border-t border-border">
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <button
                    className="w-full text-center text-sm text-muted-foreground hover:text-destructive transition-colors"
                    data-testid="forgot-pin-btn"
                  >
                    Forgot Passcode?
                  </button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle className="flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-destructive" />
                      Reset App?
                    </AlertDialogTitle>
                    <AlertDialogDescription className="space-y-2">
                      <p>
                        There is no way to recover a forgotten passcode. Resetting will 
                        <strong className="text-destructive"> permanently erase all your inventory data</strong>, 
                        including firearms, ammunition, accessories, documents, and wishlist items.
                      </p>
                      <p className="font-medium">
                        This action cannot be undone.
                      </p>
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel disabled={wiping}>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleReset}
                      disabled={wiping}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      data-testid="confirm-reset-btn"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      {wiping ? 'Erasing...' : 'Erase All Data & Reset'}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-sm font-bold text-muted-foreground mt-6">
          YOUR DATA IS ONLY STORED ON THIS DEVICE
        </p>
      </div>

      {/* Version */}
      <footer className="py-4 text-center">
        <span className="text-xs text-muted-foreground/60">Version 1.1</span>
      </footer>
    </div>
  );
}
